import { useMemo, useState } from 'react';
import {
  Button,
  ErrorState,
  Footnote,
  FormGroup,
  FormRow,
  Group,
  Notice,
  Row,
  SectionLabel,
  Skeleton,
  StatusPill,
} from '@store/ui';
import { haptic } from '../../telegram';
import { useAsync } from '../shared';
import {
  adminProduct,
  inventoryImport,
  inventoryPreview,
  newIdempotencyKey,
  INVENTORY_TYPES,
  type ImportPreview,
  type ImportResult,
  type InventoryType,
} from './client';
import { ActionBar, AdminHeader, ChoiceRows, TextRow, useSubmit } from './fields';

// --- Склад -------------------------------------------------------------------

/**
 * The same rule `inventoryService` applies before it will store anything:
 * `trimmed.length >= 4 && trimmed.length <= 4096`. Restated here so the counts
 * on screen are the counts the server will reach, rather than an optimistic
 * total that shrinks the moment the owner presses the button.
 */
const isPlausible = (value: string): boolean => value.length >= 4 && value.length <= 4096;

interface Tally {
  readonly payloads: string[];
  readonly total: number;
  readonly valid: number;
  readonly duplicates: number;
  readonly empty: number;
  readonly malformed: number;
}

/**
 * What is in the box, counted before anything is sent.
 *
 * Operators paste thousands of codes out of a spreadsheet, and a paste always
 * carries blank lines and a stray header row. Naming each kind of line — and
 * the number of them — is the difference between «загружено 4980 из 5000» after
 * the fact and knowing what will happen while it can still be fixed.
 */
function tally(raw: string): Tally {
  const seen = new Set<string>();
  const payloads: string[] = [];
  let empty = 0;
  let malformed = 0;
  let duplicates = 0;
  let total = 0;

  for (const line of raw.split(/\r?\n/)) {
    const value = line.trim();
    if (value === '') {
      empty += 1;
      continue;
    }
    total += 1;
    if (!isPlausible(value)) {
      malformed += 1;
      continue;
    }
    if (seen.has(value)) {
      duplicates += 1;
      continue;
    }
    seen.add(value);
    payloads.push(value);
  }

  return { payloads, total, valid: payloads.length, duplicates, empty, malformed };
}

/** The batch name the import is filed under; the owner may rename it. */
const defaultLabel = (): string =>
  `Импорт ${new Date().toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' })}`;

const IMPORT_LIMIT = 5000;

export function AdminStockScreen({
  productId,
  onNavigate,
}: {
  readonly productId: string;
  readonly onNavigate: (route: string) => void;
}) {
  const product = useAsync(() => adminProduct(productId), [productId]);
  const [raw, setRaw] = useState('');
  const [itemType, setItemType] = useState<InventoryType>('license_key');
  const [variantId, setVariantId] = useState<string | null>(null);
  const [label, setLabel] = useState(defaultLabel);
  const [preview, setPreview] = useState<ImportPreview | null>(null);
  const [result, setResult] = useState<ImportResult | null>(null);
  /* Which of the two buttons is working, so the spinner lands on the one that
     was pressed rather than on both. */
  const [running, setRunning] = useState<'check' | 'upload' | null>(null);
  const { busy, error, setError, run } = useSubmit();

  const counts = useMemo(() => tally(raw), [raw]);
  const variants = (product.data?.variants ?? []).filter((variant) => variant.isPublished);
  const overLimit = counts.valid > IMPORT_LIMIT;

  /* Any edit invalidates the server's verdict: a preview that describes a
     different paste is worse than no preview, because it will be believed. */
  const onPaste = (value: string) => {
    setRaw(value);
    setPreview(null);
    setResult(null);
    setError(null);
  };

  const check = () => {
    setRunning('check');
    return run(async () => {
      const { preview: checked } = await inventoryPreview({
        productId,
        variantId,
        itemType,
        label: label.trim() || defaultLabel(),
        payloads: counts.payloads,
      });
      setPreview(checked);
    }).finally(() => setRunning(null));
  };

  const upload = () => {
    setRunning('upload');
    return run(async () => {
      const { result: done } = await inventoryImport(
        {
          productId,
          variantId,
          itemType,
          label: label.trim() || defaultLabel(),
          payloads: counts.payloads,
        },
        newIdempotencyKey(),
      );
      haptic.success();
      setResult(done);
      setPreview(null);
      /* Cleared only now. The values are sellable secrets and there is no
         reason for them to stay on a screen the owner will hand back to their
         phone — but a failed import keeps every line, because retyping five
         thousand codes is not a recovery path. */
      setRaw('');
      product.reload();
    }).finally(() => setRunning(null));
  };

  if (product.loading) {
    return (
      <div className="container stack">
        <Skeleton height={34} width="55%" />
        <Skeleton height={180} />
      </div>
    );
  }

  if (product.error || !product.data) {
    return (
      <div className="container stack">
        <AdminHeader title="Склад" />
        <ErrorState
          title="Товар не открылся"
          text={product.error ?? undefined}
          onRetry={product.reload}
        />
      </div>
    );
  }

  if (!product.data.tracksInventory) {
    return (
      <div className="container stack">
        <AdminHeader title="Склад" subtitle={product.data.title} />
        <Notice tone="warning">
          У этого товара выключен учёт остатков — складу неоткуда браться и незачем. Включите его в
          разделе «Склад и выдача», если товар выдаётся ключами.
        </Notice>
        <Group>
          <Row
            title="Открыть карточку товара"
            chevron
            onClick={() => onNavigate(`/admin/products/${productId}`)}
          />
        </Group>
      </div>
    );
  }

  return (
    <div className="container stack">
      <AdminHeader title="Склад" subtitle={product.data.title} />

      <Group>
        <Row
          title="Сейчас доступно"
          trailing={
            product.data.available === 0 ? (
              <StatusPill tone="danger">Пусто</StatusPill>
            ) : (
              <span className="price">{product.data.available ?? 0}</span>
            )
          }
        />
      </Group>

      {variants.length > 0 ? (
        <section>
          <SectionLabel>Куда загрузить</SectionLabel>
          <Group>
            <Row
              title="Общий склад товара"
              subtitle="Для покупателей, которые не выбирают вариант"
              selected={variantId === null}
              onClick={() => {
                haptic.select();
                setVariantId(null);
              }}
            />
            {variants.map((variant) => (
              <Row
                key={variant.id}
                title={variant.title}
                subtitle={`Остаток ${variant.available ?? 0}`}
                selected={variantId === variant.id}
                onClick={() => {
                  haptic.select();
                  setVariantId(variant.id);
                }}
              />
            ))}
          </Group>
          <Footnote>
            У варианта свой склад. Ключи, загруженные в общий склад, не подойдут покупателю, который
            выбрал вариант.
          </Footnote>
        </section>
      ) : null}

      <section>
        <SectionLabel>Что загружаем</SectionLabel>
        <ChoiceRows
          options={INVENTORY_TYPES.map((type) => ({ value: type.value, label: type.label }))}
          value={itemType}
          onChange={setItemType}
        />
      </section>

      <FormGroup>
        <TextRow
          label="Название партии"
          id="stock-label"
          value={label}
          onChange={setLabel}
          placeholder={defaultLabel()}
          maxLength={160}
        />
      </FormGroup>
      <Footnote>По названию партию потом видно в истории склада.</Footnote>

      <section>
        <SectionLabel>Значения</SectionLabel>
        <FormGroup>
          <FormRow label="По одному в строке" htmlFor="stock-payloads" stacked>
            <textarea
              id="stock-payloads"
              rows={8}
              value={raw}
              onChange={(event) => onPaste(event.target.value)}
              placeholder={'XXXX-XXXX-XXXX\nYYYY-YYYY-YYYY'}
              spellCheck={false}
              autoCapitalize="off"
              autoCorrect="off"
            />
          </FormRow>
        </FormGroup>
      </section>

      {/*
       * The verdict before the button, always — this is the whole point of the
       * screen. The three failing counts are only drawn when they are not zero:
       * a row of zeroes reads as a problem report and there is no problem.
       */}
      {counts.total > 0 || counts.empty > 0 ? (
        <Group padded>
          <div className="stat-row">
            <div className="stat">
              <span className="stat__value">{counts.valid}</span>
              <span className="stat__label">загрузим</span>
            </div>
            {counts.duplicates > 0 ? (
              <div className="stat">
                <span className="stat__value">{counts.duplicates}</span>
                <span className="stat__label">повторов</span>
              </div>
            ) : null}
            {counts.malformed > 0 ? (
              <div className="stat">
                <span className="stat__value">{counts.malformed}</span>
                <span className="stat__label">битых</span>
              </div>
            ) : null}
            {counts.empty > 0 ? (
              <div className="stat">
                <span className="stat__value">{counts.empty}</span>
                <span className="stat__label">пустых</span>
              </div>
            ) : null}
          </div>
        </Group>
      ) : null}

      {counts.malformed > 0 ? (
        <Footnote>
          Слишком короткие или слишком длинные строки магазин не примет: значение — от 4 до 4096
          символов. Такие строки просто не загрузятся.
        </Footnote>
      ) : null}

      {overLimit ? (
        <Notice tone="warning">
          За один раз можно загрузить не больше {IMPORT_LIMIT} значений. Разбейте список на части.
        </Notice>
      ) : null}

      {/* The server's own count, which knows one thing this screen cannot: what
          is already lying in stock from a previous import. */}
      {preview ? (
        <Group>
          <Row title="Новых значений" trailing={<span className="price">{preview.unique}</span>} />
          {preview.alreadyInStock > 0 ? (
            <Row
              title="Уже на складе"
              subtitle="Повторно не загрузятся"
              trailing={<span className="price">{preview.alreadyInStock}</span>}
            />
          ) : null}
          {preview.samples.length > 0 ? (
            <Row title="Как они выглядят" subtitle={preview.samples.join(' · ')} />
          ) : null}
        </Group>
      ) : null}

      {result ? (
        <section>
          <SectionLabel>Загружено</SectionLabel>
          <Group>
            <Row title="Добавлено" trailing={<span className="price">{result.imported}</span>} />
            {result.duplicates > 0 ? (
              <Row
                title="Повторов пропущено"
                trailing={<span className="price">{result.duplicates}</span>}
              />
            ) : null}
            {result.invalid > 0 ? (
              <Row title="Не приняли" trailing={<span className="price">{result.invalid}</span>} />
            ) : null}
            <Row
              icon="receipt"
              title="К карточке товара"
              chevron
              onClick={() => onNavigate(`/admin/products/${productId}`)}
            />
          </Group>
        </section>
      ) : null}

      <ActionBar error={error}>
        <div className="row" style={{ gap: 'var(--space-2)' }}>
          <Button
            variant="secondary"
            block
            loading={running === 'check'}
            disabled={busy || counts.valid === 0 || overLimit}
            onClick={() => void check()}
          >
            Проверить
          </Button>
          <Button
            block
            loading={running === 'upload'}
            disabled={busy || counts.valid === 0 || overLimit}
            onClick={() => void upload()}
          >
            Загрузить {counts.valid}
          </Button>
        </div>
      </ActionBar>
    </div>
  );
}
